import { useArsTable } from "./ArsTableContextProvider";
import styles from "./ArsTableTree.module.scss";
import { ArsTableTreeNode, ArsTableTreeNodeProps } from "./ArsTableTreeNode";
import { groupToTreeNode } from "./functions";

export interface ArsTableTreeViewSideProps<T> {
  label: string;
  nodes: ArsTableTreeNodeProps<T>[];
  defaultExpanded?: boolean;
}

/**
 * Renders one side of the ArsTableTreeView (either rows or columns) with a heading and tree nodes.
 *
 * @param props - Component properties
 * @param props.label - Heading label for the tree side (e.g., "Rows" or "Columns")
 * @param props.nodes - Array of tree nodes to render under this side
 * @param props.defaultExpanded - Whether tree nodes should be expanded by default (default: true)
 * @returns Rendered tree view side component
 */
const ArsTableTreeViewSide = <T,>({
  label,
  nodes,
  defaultExpanded,
}: ArsTableTreeViewSideProps<T>) => {
  return (
    <div className={styles["mos-ars-body-tree-section"]}>
      <h3 className={styles["mos-ars-body-tree-heading"]}>{label}</h3>
      <div className={styles["mos-ars-body-tree-content"]}>
        {nodes.length === 0 ? (
          <p className={styles["mos-ars-body-tree-empty"]}>No groups defined</p>
        ) : (
          nodes.map((node) => (
            <ArsTableTreeNode
              key={node.id}
              defaultExpanded={defaultExpanded}
              {...node}
            />
          ))
        )}
      </div>
    </div>
  );
};

ArsTableTreeViewSide.displayName = "ArsTableTreeViewSide";

export interface ArsTableTreeViewProps {
  defaultExpanded?: boolean;
}

/**
 * Displays ArsTableBody rows and columns in an expandable tree view.
 *
 * @param props - Component properties
 * @param props.defaultExpanded - Whether tree nodes should be expanded by default (default: true)
 * @returns Rendered tree view for the body data
 */
export const ArsTableTreeView = <T,>({ defaultExpanded = true }: ArsTableTreeViewProps) => {
  const { state } = useArsTable<T>();
  if (!state) return null;

  const rowNodes = state.bodyData.rows.map((row) =>
    groupToTreeNode(row, state.settings.editorProps.isT),
  );
  const columnNodes = state.bodyData.columns.map((col) =>
    groupToTreeNode(col, state.settings.editorProps.isT),
  );

  return (
    <div className={styles["mos-ars-body-tree-view"]}>
      <ArsTableTreeViewSide
        label="Rows"
        nodes={rowNodes}
        defaultExpanded={defaultExpanded}
      />
      <ArsTableTreeViewSide
        label="Columns"
        nodes={columnNodes}
        defaultExpanded={defaultExpanded}
      />
    </div>
  );
};

ArsTableTreeView.displayName = "ArsTableTreeView";
